import { gql, useMutation } from '@apollo/client';
import type { ApolloError, FetchResult } from '@apollo/client';
import { Str } from '@livecontrol/core-utils';
import type { Event } from '@livecontrol/scheduler/model';
import { Account } from '@livecontrol/scheduler/model';
import { useCallback, useState } from 'react';
// eslint-disable-next-line import/no-internal-modules
import { QUERY } from '../account/use-account';
import { Errors } from '../graphql';
import type { MutationResult } from '../graphql';
import * as Dbx from './dbx';

interface TVariables {
  eventIds?: number[];
  recurrenceIds?: string[];
  reason?: string;
}

interface TData {
  deleteEvents: {
    event_id: number;
    deleted: boolean;
  }[];
}

const MUTATION = gql`
  mutation DeleteEvents($reason: String, $recurrenceIds: [String!], $eventIds: [Int!]) {
    deleteEvents(reason: $reason, recurrence_ids: $recurrenceIds, eventIds: $eventIds) {
      event_id
      deleted
    }
  }
`;

interface Args {
  events: Event | Event[];
  reason?: string;
  account: Account.Like;
  useRecurrenceIds?: boolean;
}

interface Summary {
  success: number[];
  failure: number[];
}

export const useDelete = (): [
  (args: Args) => Promise<Summary | undefined>,
  MutationResult<Summary, 'summary'>
] => {
  const [mutation, result] = useMutation<TData, TVariables>(MUTATION);
  const [error, setError] = useState<Error | undefined>();
  const [summary, setSummary] = useState<Summary | undefined>();

  return [
    useCallback(
      async (args: Args): Promise<Summary | undefined> => {
        let summary_: Summary | undefined;

        try {
          // Parse the input arguments
          const events = Array.isArray(args.events) ? args.events : [args.events];

          const variables: TVariables = {
            reason: Str.normalize(args.reason)
          };

          if (args.useRecurrenceIds) {
            // Separar eventos por recurrenceId
            const eventsWithoutRecurrence: number[] = [];
            const recurrenceIdsSet = new Set<string>();

            events.forEach(event => {
              if (event.recurrenceId) {
                recurrenceIdsSet.add(event.recurrenceId);
              } else {
                const eventId = typeof event === 'number' ? event : event.id;

                if (eventId) {
                  eventsWithoutRecurrence.push(eventId);
                }
              }
            });

            // Asignar solo si hay valores
            if (eventsWithoutRecurrence.length > 0) {
              variables.eventIds = eventsWithoutRecurrence;
            }

            if (recurrenceIdsSet.size > 0) {
              variables.recurrenceIds = [...recurrenceIdsSet];
            }
          } else {
            // Comportamiento original: solo usar eventIds
            variables.eventIds = Dbx.toEventIds(args.events);
          }

          // Validar que hay algo que eliminar
          if ((variables.eventIds && variables.eventIds.length > 0) || 
              (variables.recurrenceIds && variables.recurrenceIds.length > 0)) {
            // Execute the GraphQL mutation
            const response = await mutation({
              variables,
              refetchQueries: [
                {
                  query: QUERY,
                  variables: {
                    id: Account.toId(args.account)!
                  }
                }
              ]
            })
              .then(({ data }: FetchResult<TData>) => data?.deleteEvents)
              .catch((_error: ApolloError) => {
                throw Errors.serverError();
              });

            if (response) {
              // Parse the server response
              summary_ = {
                success: [],
                failure: []
              };

              for (const item of response) {
                summary_[item.deleted ? 'success' : 'failure'].push(item.event_id);
              }
            }
          }
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        setSummary(summary_);

        return summary_;
      },
      [mutation, setError, setSummary]
    ),
    {
      summary,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
